import React, { useState, useEffect } from 'react';
import { normalizeProjectName } from '../utils/validation';
import {
    Plus,
    Edit,
    Trash2,
    Send,
    Check,
    X,
    RotateCcw,
    Eye,
    FileText,
    CheckCircle
} from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { useAppContext } from '../contexts/AppContext';

export interface ApprovalRecord {
    reviewer: string;
    role: string;
    decision: 'Approved' | 'Rejected' | 'Conditional' | 'Pending';
    date: string;
    comments: string;
}

export interface PbsChangeSummary {
    itemId: string;
    pbsNumber: string;
    itemName: string;
    changeType: 'added' | 'removed' | 'modified';
    changes: {
        field: string;
        oldValue: any;
        newValue: any;
    }[];
}

export interface ECNRecord {
    ecnNumber: string;
    ecnTitle: string;
    ecnType: 'Design Change' | 'Material Change' | 'Process Change' | 'Supplier Change' | 'Documentation';
    priority: 'Critical' | 'High' | 'Medium' | 'Low';
    status: 'Draft' | 'Submitted' | 'Under Review' | 'Approved' | 'Rejected' | 'Implemented' | 'Closed';

    submittedBy: string;
    submittedDate: string;
    reviewedBy: string[];
    approvedBy: string;
    approvedDate: string;
    implementedDate: string;
    closedDate?: string;

    changeDescription: string;
    reasonForChange: string;
    affectedDocuments: string[];
    affectedItems: string[];
    previousConfiguration: string;
    newConfiguration: string;

    costImpact: {
        tooling: number;
        material: number;
        labor: number;
        total: number;
    };
    scheduleImpact: string;
    qualityImpact: string;
    validationRequired: boolean;
    testingRequired: string[];

    // PBS Revision Integration (NEW)
    pbsRevisionId?: string;          // Reference to PBS revision
    pbsRevisionName?: string;        // e.g., "Beta 2"
    previousRevisionId?: string;     // Source revision for comparison
    affectedPbsItems?: string[];     // PBS item IDs
    pbsChangeType?: 'added' | 'removed' | 'modified';
    pbsChangeSummary?: PbsChangeSummary[];

    linkedFmeaIds?: string[];
    linkedBomIds?: string[];
    linkedMaterialIds?: string[];
    linkedIterationNumber?: number;

    attachments?: string[];
    relatedECNs?: string[];
    dispositionOfExistingParts?: string;

    approvalHistory: ApprovalRecord[];

    // Metadata Standards v2.0.0
    projectName?: string;           // Project this ECN belongs to
    epochId?: number;               // Epoch when ECN raised
}

export default function ECNManagement() {
    const { allData, currentUser, activeProject, updateEcnLog } = useAppContext();
    const [filteredEcns, setFilteredEcns] = useState<ECNRecord[]>([]);
    const [selectedECN, setSelectedECN] = useState<ECNRecord | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Filters
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [priorityFilter, setPriorityFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState('');

    // Get ECNs from AppContext (single source of truth)
    const ecns = allData.ecnLog || [];

    useEffect(() => {
        // Initialize ECN log if empty (for first-time use)
        if (!allData.ecnLog || allData.ecnLog.length === 0) {
            // Could load seed data here if needed
            console.log('ECN log initialized from AppContext');
        }
    }, []);

    useEffect(() => {
        // Apply filters
        let filtered = ecns;

        // Filter by active project (Normalize for robustness)
        if (activeProject !== 'all') {
            const activeProjectNormalized = normalizeProjectName(activeProject);
            filtered = filtered.filter(e => {
                if (!e.projectName) return true; // Orphaned ECNs show everywhere
                const itemProjectNormalized = normalizeProjectName(e.projectName);
                return itemProjectNormalized === activeProjectNormalized;
            });
        }

        if (statusFilter !== 'all') {
            if (statusFilter === 'Implemented') {
                // For 'Implemented' filter, also show 'Closed' as they are terminal implemented states
                filtered = filtered.filter(e => e.status === 'Implemented' || e.status === 'Closed');
            } else {
                filtered = filtered.filter(e => e.status === statusFilter);
            }
        }

        if (priorityFilter !== 'all') {
            filtered = filtered.filter(e => e.priority === priorityFilter);
        }

        if (typeFilter !== 'all') {
            filtered = filtered.filter(e => e.ecnType === typeFilter);
        }

        if (searchTerm) {
            filtered = filtered.filter(e =>
                e.ecnNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                e.ecnTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                e.changeDescription.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        setFilteredEcns(filtered);
    }, [ecns, activeProject, statusFilter, priorityFilter, typeFilter, searchTerm]);

    const saveECNs = (updatedEcns: ECNRecord[]) => {
        updateEcnLog(updatedEcns);
    };

    const generateECNNumber = () => {
        const year = new Date().getFullYear();
        const existingNumbers = ecns
            .filter(e => e.ecnNumber.startsWith(`ECN-${year}`))
            .map(e => parseInt(e.ecnNumber.split('-')[2]))
            .filter(n => !isNaN(n));

        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
        return `ECN-${year}-${String(nextNumber).padStart(3, '0')}`;
    };

    const createNewECN = () => {
        const newECN: ECNRecord = {
            ecnNumber: generateECNNumber(),
            ecnTitle: '',
            ecnType: 'Design Change',
            priority: 'Medium',
            status: 'Draft',
            submittedBy: currentUser.initials,
            submittedDate: '',
            reviewedBy: [],
            approvedBy: '',
            approvedDate: '',
            implementedDate: '',
            changeDescription: '',
            reasonForChange: '',
            affectedDocuments: [],
            affectedItems: [],
            previousConfiguration: '',
            newConfiguration: '',
            costImpact: {
                tooling: 0,
                material: 0,
                labor: 0,
                total: 0
            },
            scheduleImpact: '',
            qualityImpact: '',
            validationRequired: false,
            testingRequired: [],
            approvalHistory: [],
            // Metadata Standards v2.0.0
            projectName: activeProject !== 'all' ? activeProject : undefined,
            epochId: allData.projectMetadata?.epochId || 0
        };

        setSelectedECN(newECN);
        setIsEditing(false);
        setShowForm(true);
    };

    const editECN = (ecn: ECNRecord) => {
        if (ecn.status !== 'Draft' && ecn.status !== 'Rejected') {
            alert('Can only edit Draft or Rejected ECNs');
            return;
        }
        setSelectedECN({ ...ecn });
        setIsEditing(true);
        setShowForm(true);
    };

    const deleteECN = (ecnNumber: string) => {
        const ecn = ecns.find(e => e.ecnNumber === ecnNumber);
        if (ecn && ecn.status !== 'Draft') {
            alert('Can only delete Draft ECNs');
            return;
        }

        if (confirm(`Delete ECN ${ecnNumber}?`)) {
            const updated = ecns.filter(e => e.ecnNumber !== ecnNumber);
            saveECNs(updated);
        }
    };

    const submitForReview = (ecnNumber: string) => {
        const ecn = ecns.find(e => e.ecnNumber === ecnNumber);
        if (!ecn || ecn.status !== 'Draft') return;

        const updated = ecns.map(e =>
            e.ecnNumber === ecnNumber
                ? { ...e, status: 'Submitted' as const, submittedDate: new Date().toISOString().split('T')[0] }
                : e
        );
        saveECNs(updated);
    };

    const recallECN = (ecnNumber: string) => {
        const ecn = ecns.find(e => e.ecnNumber === ecnNumber);
        if (!ecn || ecn.status !== 'Submitted') {
            alert('Can only recall Submitted ECNs');
            return;
        }

        if (confirm('Recall this ECN back to Draft status?')) {
            const updated = ecns.map(e =>
                e.ecnNumber === ecnNumber
                    ? { ...e, status: 'Draft' as const, submittedDate: '' }
                    : e
            );
            saveECNs(updated);
        }
    };

    const reviewECN = (ecnNumber: string, decision: 'Approved' | 'Rejected', comments: string) => {
        const ecn = ecns.find(e => e.ecnNumber === ecnNumber);
        if (!ecn) return;

        const approvalRecord: ApprovalRecord = {
            reviewer: currentUser.initials,
            role: currentUser.role,
            decision,
            date: new Date().toISOString().split('T')[0],
            comments
        };

        const updated = ecns.map(e => {
            if (e.ecnNumber === ecnNumber) {
                const newHistory = [...e.approvalHistory, approvalRecord];
                return {
                    ...e,
                    status: decision === 'Approved' ? 'Approved' as const : 'Rejected' as const,
                    approvedBy: decision === 'Approved' ? currentUser.initials : '',
                    approvedDate: decision === 'Approved' ? approvalRecord.date : '',
                    approvalHistory: newHistory
                };
            }
            return e;
        });

        saveECNs(updated);
    };

    const markImplemented = (ecnNumber: string) => {
        const updated = ecns.map(e =>
            e.ecnNumber === ecnNumber
                ? { ...e, status: 'Implemented' as const, implementedDate: new Date().toISOString().split('T')[0] }
                : e
        );
        saveECNs(updated);
    };

    const closeECN = (ecnNumber: string) => {
        const updated = ecns.map(e =>
            e.ecnNumber === ecnNumber
                ? { ...e, status: 'Closed' as const, closedDate: new Date().toISOString().split('T')[0] }
                : e
        );
        saveECNs(updated);
    };

    const saveECN = (ecnData: ECNRecord) => {
        // Calculate total cost
        ecnData.costImpact.total = ecnData.costImpact.tooling + ecnData.costImpact.material + ecnData.costImpact.labor;

        if (isEditing) {
            const updated = ecns.map(e => e.ecnNumber === ecnData.ecnNumber ? ecnData : e);
            saveECNs(updated);
        } else {
            saveECNs([...ecns, ecnData]);
        }

        setShowForm(false);
        setSelectedECN(null);
    };

    const getStatusColor = (status: string) => {
        const colors = {
            'Draft': 'bg-gray-100 text-gray-800 border-gray-300',
            'Submitted': 'bg-blue-100 text-blue-800 border-blue-300',
            'Under Review': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'Approved': 'bg-green-100 text-green-800 border-green-300',
            'Rejected': 'bg-red-100 text-red-800 border-red-300',
            'Implemented': 'bg-purple-100 text-purple-800 border-purple-300',
            'Closed': 'bg-slate-100 text-slate-800 border-slate-300'
        };
        return colors[status as keyof typeof colors] || colors.Draft;
    };

    const getPriorityColor = (priority: string) => {
        const colors = {
            'Critical': 'bg-red-500 text-white',
            'High': 'bg-orange-500 text-white',
            'Medium': 'bg-yellow-500 text-white',
            'Low': 'bg-green-500 text-white'
        };
        return colors[priority as keyof typeof colors] || colors.Medium;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-card rounded-lg shadow-md p-6 border border-border">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                            <FileText className="w-8 h-8 text-primary" />
                            Engineering Change Notices (ECN)
                        </h1>
                        <p className="text-text-secondary mt-2">
                            Manage design changes with full approval workflow
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <ModuleInfoModal
                            title="Engineering Change Management"
                            description="A formal workflow for proposing, reviewing, approving, and tracking design changes."
                            benefits={[
                                "Maintain a complete audit trail of design evolution.",
                                "Ensure stakeholders review critical changes.",
                                "Track cost and schedule impacts for every version."
                            ]}
                            usageInstructions={[
                                "Click 'New ECN' to draft a proposed change.",
                                "Submit drafts for review.",
                                "Reviewers can Approve, Reject, or Comment on ECNs."
                            ]}
                        />
                        <button
                            onClick={createNewECN}
                            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-secondary text-white rounded-lg transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            New ECN
                        </button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-5 gap-4 mt-4">
                    {/* Filtered stats for the active project */}
                    {(() => {
                        const projectEcns = activeProject === 'all'
                            ? ecns
                            : ecns.filter(e => {
                                if (!e.projectName) return true;
                                return normalizeProjectName(e.projectName) === normalizeProjectName(activeProject);
                            });

                        return (
                            <>
                                <div className="bg-background p-4 rounded-lg border border-border transition-all hover:border-primary/50">
                                    <div className="text-sm text-text-secondary">Project ECNs</div>
                                    <div className="text-2xl font-bold text-text-primary">{projectEcns.length}</div>
                                </div>
                                <div className="bg-background p-4 rounded-lg border border-border">
                                    <div className="text-sm text-text-secondary">Draft</div>
                                    <div className="text-2xl font-bold text-gray-600">
                                        {projectEcns.filter(e => e.status === 'Draft').length}
                                    </div>
                                </div>
                                <div className="bg-background p-4 rounded-lg border border-border text-blue-600">
                                    <div className="text-sm text-text-secondary">Pending Review</div>
                                    <div className="text-2xl font-bold">
                                        {projectEcns.filter(e => e.status === 'Submitted' || e.status === 'Under Review').length}
                                    </div>
                                </div>
                                <div className="bg-background p-4 rounded-lg border border-border text-green-600">
                                    <div className="text-sm text-text-secondary">Approved</div>
                                    <div className="text-2xl font-bold">
                                        {projectEcns.filter(e => e.status === 'Approved').length}
                                    </div>
                                </div>
                                <div className="bg-background p-4 rounded-lg border border-border text-purple-600">
                                    <div className="text-sm text-text-secondary">Implemented/Closed</div>
                                    <div className="text-2xl font-bold">
                                        {projectEcns.filter(e => e.status === 'Implemented' || e.status === 'Closed').length}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-card rounded-lg shadow-md p-4 border border-border">
                <div className="grid grid-cols-4 gap-4">
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="ECN number, title..."
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="all">All Status</option>
                            <option value="Draft">Draft</option>
                            <option value="Submitted">Submitted</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Implemented">Implemented</option>
                            <option value="Closed">Closed</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Priority</label>
                        <select
                            value={priorityFilter}
                            onChange={(e) => setPriorityFilter(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="all">All Priorities</option>
                            <option value="Critical">Critical</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-sm text-text-secondary mb-2 block">Type</label>
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                        >
                            <option value="all">All Types</option>
                            <option value="Design Change">Design Change</option>
                            <option value="Material Change">Material Change</option>
                            <option value="Process Change">Process Change</option>
                            <option value="Supplier Change">Supplier Change</option>
                            <option value="Documentation">Documentation</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* ECN Table */}
            <div className="bg-card rounded-lg shadow-md border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-background">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">ECN Number</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Title</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Type</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Priority</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Status</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Submitted By</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Mitigated Risk</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredEcns.map((ecn) => (
                                <tr key={ecn.ecnNumber} className="hover:bg-background transition-colors">
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => {
                                                setSelectedECN(ecn);
                                                setShowDetailModal(true);
                                            }}
                                            className="text-primary hover:underline font-medium"
                                        >
                                            {ecn.ecnNumber}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-text-primary">
                                        {ecn.ecnTitle || 'Untitled'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-text-secondary">{ecn.ecnType}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getPriorityColor(ecn.priority)}`}>
                                            {ecn.priority}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(ecn.status)}`}>
                                            {ecn.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-text-secondary">{ecn.submittedBy}</td>
                                    <td className="px-4 py-3 text-sm text-text-secondary">
                                        {ecn.submittedDate || '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1">
                                            {(ecn.linkedFmeaIds || []).slice(0, 2).map(fId => {
                                                const risk = allData.fmeaItems.find(f => f.id === fId);
                                                return (
                                                    <div key={fId} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100 truncate max-w-[150px]" title={risk?.failureMode || fId}>
                                                        {risk?.failureMode || fId}
                                                    </div>
                                                );
                                            })}
                                            {(ecn.linkedFmeaIds || []).length > 2 && (
                                                <div className="text-[10px] text-text-secondary italic">
                                                    +{(ecn.linkedFmeaIds || []).length - 2} more
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex gap-2">
                                            {ecn.status === 'Draft' && (
                                                <>
                                                    <button
                                                        onClick={() => editECN(ecn)}
                                                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => submitForReview(ecn.ecnNumber)}
                                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                                        title="Submit for Review"
                                                    >
                                                        <Send className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteECN(ecn.ecnNumber)}
                                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}
                                            {ecn.status === 'Rejected' && (
                                                <button
                                                    onClick={() => editECN(ecn)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="Edit & Resubmit"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                            )}
                                            {ecn.status === 'Submitted' && (
                                                <button
                                                    onClick={() => recallECN(ecn.ecnNumber)}
                                                    className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                                    title="Recall to Draft"
                                                >
                                                    <RotateCcw className="w-4 h-4" />
                                                </button>
                                            )}
                                            {ecn.status === 'Approved' && (
                                                <button
                                                    onClick={() => markImplemented(ecn.ecnNumber)}
                                                    className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                                                    title="Mark as Implemented"
                                                >
                                                    <CheckCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                            {ecn.status === 'Implemented' && (
                                                <button
                                                    onClick={() => closeECN(ecn.ecnNumber)}
                                                    className="p-1.5 text-slate-600 hover:bg-slate-50 rounded transition-colors"
                                                    title="Close ECN"
                                                >
                                                    <Check className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    setSelectedECN(ecn);
                                                    setShowDetailModal(true);
                                                }}
                                                className="p-1.5 text-gray-600 hover:bg-gray-50 rounded transition-colors"
                                                title="View Details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ECN Form Modal */}
            {showForm && selectedECN && (
                <ECNFormModal
                    ecn={selectedECN}
                    isEditing={isEditing}
                    onSave={saveECN}
                    onCancel={() => {
                        setShowForm(false);
                        setSelectedECN(null);
                    }}
                />
            )}

            {/* ECN Detail Modal */}
            {showDetailModal && selectedECN && (
                <ECNDetailModal
                    ecn={selectedECN}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedECN(null);
                    }}
                    onReview={(decision, comments) => {
                        reviewECN(selectedECN.ecnNumber, decision, comments);
                        setShowDetailModal(false);
                        setSelectedECN(null);
                    }}
                    currentUser={currentUser}
                />
            )}
        </div>
    );
}

// ECN Form Modal Component
function ECNFormModal({ ecn, isEditing, onSave, onCancel }: {
    ecn: ECNRecord;
    isEditing: boolean;
    onSave: (ecn: ECNRecord) => void;
    onCancel: () => void;
}) {
    const { allData } = useAppContext();
    const [formData, setFormData] = useState<ECNRecord>(ecn);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel}>
            <div
                className="bg-card border border-border rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold text-text-primary mb-6">
                    {isEditing ? `Edit ECN: ${formData.ecnNumber}` : `New ECN: ${formData.ecnNumber}`}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                ECN Title *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.ecnTitle}
                                onChange={(e) => setFormData({ ...formData, ecnTitle: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                placeholder="Brief title describing the change"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                ECN Type *
                            </label>
                            <select
                                required
                                value={formData.ecnType}
                                onChange={(e) => setFormData({ ...formData, ecnType: e.target.value as any })}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            >
                                <option value="Design Change">Design Change</option>
                                <option value="Material Change">Material Change</option>
                                <option value="Process Change">Process Change</option>
                                <option value="Supplier Change">Supplier Change</option>
                                <option value="Documentation">Documentation</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Priority *
                            </label>
                            <select
                                required
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value as any })}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            >
                                <option value="Critical">Critical</option>
                                <option value="High">High</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low</option>
                            </select>
                        </div>
                    </div>

                    {/* Change Description */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Change Description *
                        </label>
                        <textarea
                            required
                            value={formData.changeDescription}
                            onChange={(e) => setFormData({ ...formData, changeDescription: e.target.value })}
                            rows={4}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            placeholder="Detailed description of what is being changed..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                            Reason for Change *
                        </label>
                        <textarea
                            required
                            value={formData.reasonForChange}
                            onChange={(e) => setFormData({ ...formData, reasonForChange: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                            placeholder="Why is this change necessary? What problem does it solve?"
                        />
                    </div>

                    {/* Configuration */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Previous Configuration
                            </label>
                            <textarea
                                value={formData.previousConfiguration}
                                onChange={(e) => setFormData({ ...formData, previousConfiguration: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                placeholder="Current/existing design or process..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                New Configuration *
                            </label>
                            <textarea
                                required
                                value={formData.newConfiguration}
                                onChange={(e) => setFormData({ ...formData, newConfiguration: e.target.value })}
                                rows={3}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                placeholder="New/proposed design or process..."
                            />
                        </div>
                    </div>

                    {/* Cost Impact */}
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-3">
                            Cost Impact ($)
                        </label>
                        <div className="grid grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Tooling</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.costImpact.tooling}
                                    onChange={(e) => setFormData({ ...formData, costImpact: { ...formData.costImpact, tooling: parseFloat(e.target.value) || 0 } })}
                                    className="w-full px-3 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Material</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.costImpact.material}
                                    onChange={(e) => setFormData({ ...formData, costImpact: { ...formData.costImpact, material: parseFloat(e.target.value) || 0 } })}
                                    className="w-full px-3 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Labor</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={formData.costImpact.labor}
                                    onChange={(e) => setFormData({ ...formData, costImpact: { ...formData.costImpact, labor: parseFloat(e.target.value) || 0 } })}
                                    className="w-full px-3 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-text-secondary mb-1">Total</label>
                                <input
                                    type="number"
                                    disabled
                                    value={(formData.costImpact.tooling + formData.costImpact.material + formData.costImpact.labor).toFixed(2)}
                                    className="w-full px-3 py-2 bg-gray-100 border border-border rounded"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Impact */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Schedule Impact
                            </label>
                            <input
                                type="text"
                                value={formData.scheduleImpact}
                                onChange={(e) => setFormData({ ...formData, scheduleImpact: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                placeholder="e.g., 2 weeks for implementation"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Quality Impact
                            </label>
                            <input
                                type="text"
                                value={formData.qualityImpact}
                                onChange={(e) => setFormData({ ...formData, qualityImpact: e.target.value })}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none"
                                placeholder="e.g., Reduces RPN by 50%"
                            />
                        </div>
                    </div>

                    {/* Validation */}
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="validationRequired"
                            checked={formData.validationRequired}
                            onChange={(e) => setFormData({ ...formData, validationRequired: e.target.checked })}
                            className="w-4 h-4"
                        />
                        <label htmlFor="validationRequired" className="text-sm text-text-primary">
                            Validation Testing Required
                        </label>
                    </div>

                    {/* Links */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Linked FMEA Risks
                            </label>
                            <select
                                multiple
                                value={formData.linkedFmeaIds || []}
                                onChange={(e) => {
                                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setFormData({ ...formData, linkedFmeaIds: selected });
                                }}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none h-24"
                            >
                                {(allData.fmeaItems || []).map(f => (
                                    <option key={f.id} value={f.id}>{f.id} - {f.failureMode}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-text-secondary mt-1">Hold Ctrl/Cmd to select multiple</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Linked BOM Items
                            </label>
                            <select
                                multiple
                                value={formData.linkedBomIds || []}
                                onChange={(e) => {
                                    const selected = Array.from(e.target.selectedOptions).map(o => o.value);
                                    setFormData({ ...formData, linkedBomIds: selected });
                                }}
                                className="w-full px-4 py-2 bg-background border border-border rounded focus:ring-2 focus:ring-primary outline-none h-24"
                            >
                                {(allData.bomItems || []).map(b => (
                                    <option key={String(b.id)} value={String(b.id)}>{b.component}</option>
                                ))}
                            </select>
                            <p className="text-[10px] text-text-secondary mt-1">Hold Ctrl/Cmd to select multiple</p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pt-4 border-t border-border">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="flex-1 px-4 py-2 bg-background hover:bg-gray-200 rounded transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-primary hover:bg-secondary text-white rounded transition-colors"
                        >
                            {isEditing ? 'Save Changes' : 'Create ECN'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ECN Detail Modal Component
function ECNDetailModal({ ecn, onClose, onReview, currentUser }: {
    ecn: ECNRecord;
    onClose: () => void;
    onReview: (decision: 'Approved' | 'Rejected', comments: string) => void;
    currentUser: { initials?: string; username: string; role: string };
}) {
    // Log context for debugging/audit
    if (currentUser) {
        console.debug(`ECN ${ecn.ecnNumber} viewed by ${currentUser.username}`);
    }
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [reviewDecision, setReviewDecision] = useState<'Approved' | 'Rejected'>('Approved');
    const [reviewComments, setReviewComments] = useState('');

    // Local helper functions for styling
    const getStatusColor = (status: string) => {
        const colors = {
            'Draft': 'bg-gray-100 text-gray-800 border-gray-300',
            'Submitted': 'bg-blue-100 text-blue-800 border-blue-300',
            'Under Review': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'Approved': 'bg-green-100 text-green-800 border-green-300',
            'Rejected': 'bg-red-100 text-red-800 border-red-300',
            'Implemented': 'bg-purple-100 text-purple-800 border-purple-300',
            'Closed': 'bg-slate-100 text-slate-800 border-slate-300'
        };
        return colors[status as keyof typeof colors] || colors.Draft;
    };

    const getPriorityColor = (priority: string) => {
        const colors = {
            'Critical': 'bg-red-500 text-white',
            'High': 'bg-orange-500 text-white',
            'Medium': 'bg-yellow-500 text-white',
            'Low': 'bg-green-500 text-white'
        };
        return colors[priority as keyof typeof colors] || colors.Medium;
    };

    const handleReviewSubmit = () => {
        if (!reviewComments.trim()) {
            alert('Please enter review comments');
            return;
        }
        onReview(reviewDecision, reviewComments);
        setShowReviewForm(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-card border border-border rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-text-primary">{ecn.ecnNumber}</h2>
                        <p className="text-lg text-text-secondary mt-1">{ecn.ecnTitle}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-text-secondary hover:text-text-primary"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Badges */}
                <div className="flex gap-2 mb-6">
                    <span className={`px-3 py-1 rounded text-sm font-semibold border ${getStatusColor(ecn.status)}`}>
                        {ecn.status}
                    </span>
                    <span className={`px-3 py-1 rounded text-sm font-semibold ${getPriorityColor(ecn.priority)}`}>
                        {ecn.priority}
                    </span>
                    <span className="px-3 py-1 rounded text-sm bg-gray-100 text-gray-800">
                        {ecn.ecnType}
                    </span>
                </div>

                {/* Timeline */}
                <div className="bg-background p-4 rounded-lg mb-6">
                    <h3 className="font-semibold mb-3">Timeline</h3>
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-text-secondary">Submitted:</span>
                            <span className="text-text-primary">{ecn.submittedDate || 'Not submitted'} by {ecn.submittedBy}</span>
                        </div>
                        {ecn.approvedDate && (
                            <div className="flex justify-between">
                                <span className="text-text-secondary">Approved:</span>
                                <span className="text-text-primary">{ecn.approvedDate} by {ecn.approvedBy}</span>
                            </div>
                        )}
                        {ecn.implementedDate && (
                            <div className="flex justify-between">
                                <span className="text-text-secondary">Implemented:</span>
                                <span className="text-text-primary">{ecn.implementedDate}</span>
                            </div>
                        )}
                        {ecn.closedDate && (
                            <div className="flex justify-between">
                                <span className="text-text-secondary">Closed:</span>
                                <span className="text-text-primary">{ecn.closedDate}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Change Details */}
                <div className="space-y-4 mb-6">
                    <div>
                        <h3 className="font-semibold mb-2">Change Description</h3>
                        <p className="text-text-secondary">{ecn.changeDescription}</p>
                    </div>

                    <div>
                        <h3 className="font-semibold mb-2">Reason for Change</h3>
                        <p className="text-text-secondary">{ecn.reasonForChange}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="font-semibold mb-2">Previous Configuration</h3>
                            <p className="text-text-secondary">{ecn.previousConfiguration}</p>
                        </div>
                        <div>
                            <h3 className="font-semibold mb-2">New Configuration</h3>
                            <p className="text-text-secondary">{ecn.newConfiguration}</p>
                        </div>
                    </div>
                </div>

                {/* Cost Impact */}
                <div className="bg-background p-4 rounded-lg mb-6">
                    <h3 className="font-semibold mb-3">Cost Impact</h3>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                            <div className="text-text-secondary">Tooling</div>
                            <div className="font-semibold">${ecn.costImpact.tooling}</div>
                        </div>
                        <div>
                            <div className="text-text-secondary">Material</div>
                            <div className="font-semibold">${ecn.costImpact.material.toFixed(2)}</div>
                        </div>
                        <div>
                            <div className="text-text-secondary">Labor</div>
                            <div className="font-semibold">${ecn.costImpact.labor.toFixed(2)}</div>
                        </div>
                        <div>
                            <div className="text-text-secondary">Total</div>
                            <div className="font-semibold text-lg">${ecn.costImpact.total.toFixed(2)}</div>
                        </div>
                    </div>
                </div>

                {/* Approval History */}
                {ecn.approvalHistory.length > 0 && (
                    <div className="bg-background p-4 rounded-lg mb-6">
                        <h3 className="font-semibold mb-3">Approval History</h3>
                        <div className="space-y-3">
                            {ecn.approvalHistory.map((approval, i) => (
                                <div key={i} className="border-l-4 border-primary pl-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold">{approval.reviewer}</span>
                                        <span className="text-sm text-text-secondary">({approval.role})</span>
                                        <span className={`px-2 py-0.5 rounded text-xs ${approval.decision === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                            {approval.decision}
                                        </span>
                                        <span className="text-sm text-text-secondary ml-auto">{approval.date}</span>
                                    </div>
                                    <p className="text-sm text-text-secondary italic">{approval.comments}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Review Form */}
                {ecn.status === 'Submitted' && (
                    <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
                        {!showReviewForm ? (
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="font-semibold text-yellow-800">This ECN needs your review</h4>
                                    <p className="text-sm text-yellow-700">Please review and approve or reject</p>
                                </div>
                                <button
                                    onClick={() => setShowReviewForm(true)}
                                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                                >
                                    Review Now
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <h4 className="font-semibold">Submit Review</h4>
                                <div className="flex gap-3">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={reviewDecision === 'Approved'}
                                            onChange={() => setReviewDecision('Approved')}
                                        />
                                        <span>Approve</span>
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            checked={reviewDecision === 'Rejected'}
                                            onChange={() => setReviewDecision('Rejected')}
                                        />
                                        <span>Reject</span>
                                    </label>
                                </div>
                                <textarea
                                    value={reviewComments}
                                    onChange={(e) => setReviewComments(e.target.value)}
                                    placeholder="Enter your review comments..."
                                    rows={3}
                                    className="w-full px-4 py-2 border border-yellow-300 rounded focus:ring-2 focus:ring-yellow-500 outline-none"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleReviewSubmit}
                                        className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                                    >
                                        Submit Review
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowReviewForm(false);
                                            setReviewComments('');
                                        }}
                                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Close Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-background hover:bg-gray-200 rounded transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
